import React, { useEffect, useState } from 'react';
import { getListings } from '../api';
import PublicListingCard from './PublicListingCard';

const PublicListingSection = ({ listingType, eyebrow, title, description, id = 'listings' }) => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ q: '', featured: 'all', limit: 12 });
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0, limit: 12 });

  useEffect(() => {
    let active = true;

    const load = async () => {
      try {
        setLoading(true);
        const data = await getListings({
          type: listingType,
          page: pagination.page,
          limit: filters.limit,
          q: filters.q || undefined,
          featured: filters.featured === 'all' ? undefined : filters.featured,
        });
        if (!active) return;
        setListings(data.listings || []);
        setPagination((prev) => ({
          ...prev,
          ...data.pagination,
          limit: filters.limit,
        }));
      } catch (err) {
        if (active) setError(err.response?.data?.message || 'Failed to load listings');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();

    return () => {
      active = false;
    };
  }, [listingType, pagination.page, filters.q, filters.featured, filters.limit]);

  const pages = Array.from({ length: pagination.pages || 1 }, (_v, index) => index + 1);

  return (
    <section id={id} className="w-full scroll-mt-32 border-b border-[#262626] bg-black px-5 py-12 sm:px-8 lg:px-16 lg:py-20">
      <div className="mx-auto flex max-w-[1440px] flex-col gap-[1.5rem]">
        {/* <div className="flex flex-col gap-[0.75rem] text-center">
          <p className="text-[18px] text-white">{eyebrow}</p>
          <h2 className="text-[45px] font-bold text-gradient">{title}</h2>
          <p className="mx-auto max-w-[760px] text-[17px] font-light text-white/80">{description}</p>
        </div> */}

        <div className="grid gap-[0.75rem] rounded-[18px] border border-[#262626] bg-[#111] p-[1rem] md:grid-cols-3">
          <label className="flex flex-col gap-[0.35rem] md:col-span-2">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Search</span>
            <input
              value={filters.q}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, q: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              placeholder="Search title, summary, or details"
              className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
            />
          </label>
          <label className="flex flex-col gap-[0.35rem]">
            <span className="text-[12px] font-semibold uppercase tracking-[0.12em] text-[#999]">Featured</span>
            <select
              value={filters.featured}
              onChange={(e) => {
                setFilters((prev) => ({ ...prev, featured: e.target.value }));
                setPagination((prev) => ({ ...prev, page: 1 }));
              }}
              className="rounded-lg border border-[#262626] bg-[#1A1A1A] px-[0.95rem] py-[0.8rem] text-white outline-none"
            >
              <option value="all">All listings</option>
              <option value="true">Featured only</option>
              <option value="false">Non-featured</option>
            </select>
          </label>
        </div>

        {loading ? (
          <div className="grid gap-[1rem] md:grid-cols-2 xl:grid-cols-3">
            {[1, 2, 3].map((item) => (
              <div key={item} className="h-[360px] rounded-[18px] border border-[#262626] bg-[#111] animate-pulse sm:h-[420px]" />
            ))}
          </div>
        ) : error ? (
          <div className="rounded-[18px] border border-red-900/40 bg-red-950/30 p-[1rem] text-red-200">
            {error}
          </div>
        ) : listings.length ? (
          <>
            <div className="grid gap-[1rem] md:grid-cols-2 xl:grid-cols-3">
              {listings.map((listing) => (
                <PublicListingCard
                  key={listing._id}
                  listing={listing}
                  href={`/${listingType}/${listing.slug}`}
                  label={listingType === 'rent' ? 'For Rent / Lease' : 'Property Listing'}
                />
              ))}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-[0.5rem] pt-[0.5rem]">
              <button
                type="button"
                onClick={() => setPagination((prev) => ({ ...prev, page: Math.max(prev.page - 1, 1) }))}
                disabled={pagination.page <= 1}
                className="rounded-lg border border-[#262626] px-[0.8rem] py-[0.55rem] text-[13px] font-semibold text-white disabled:opacity-50"
              >
                Prev
              </button>
              {pages.map((page) => (
                <button
                  key={page}
                  type="button"
                  onClick={() => setPagination((prev) => ({ ...prev, page }))}
                  className={`rounded-lg px-[0.85rem] py-[0.55rem] text-[13px] font-semibold ${page === pagination.page ? 'bg-[#EEC24B] text-black' : 'border border-[#262626] text-white'}`}
                >
                  {page}
                </button>
              ))}
              <button
                type="button"
                onClick={() => setPagination((prev) => ({ ...prev, page: Math.min(prev.page + 1, prev.pages || 1) }))}
                disabled={pagination.page >= pagination.pages}
                className="rounded-lg border border-[#262626] px-[0.8rem] py-[0.55rem] text-[13px] font-semibold text-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          </>
        ) : (
          <div className="rounded-[18px] border border-[#262626] bg-[#111] p-[2rem] text-center">
            <h3 className="text-[24px] font-bold text-white">No listings available</h3>
            <p className="mt-[0.75rem] text-[16px] font-light text-[#BDBDBD]">
              There are no published listings matching the current filters.
            </p>
          </div>
        )}
      </div>
    </section>
  );
};

export default PublicListingSection;
